type JsonValue = Record<string, any>;

type SelectableTeam = {
  name: string;
  value: string;
};

export type TenantSwitchToken = {
  accessToken: string;
  expiresInSeconds: number;
  tokenType: "Bearer";
};

export type TenantSwitchInput = {
  accessToken: string;
  teamId: number;
};

export type TenantSwitchDependencies = {
  getIntegradorSession: (token: TenantSwitchToken) => Promise<JsonValue | null>;
  getSyncSession: (accessToken: string, teamId: number) => Promise<JsonValue>;
  switchIntegradorTenant: (
    teamName: string,
    accessToken: string,
  ) => Promise<TenantSwitchToken>;
};

export function hasIntegradorApp(syncSession: JsonValue) {
  const apps = syncSession?.result?.apps ?? [];
  return apps.some(
    (app: JsonValue) =>
      app?.appId === "flowbits-integrador" || app?.appId === "sync-gestao",
  );
}

export function selectCurrentTeam<TTeam extends SelectableTeam>(
  teams: TTeam[],
  selectedTeamId?: string,
): TTeam | null {
  if (teams.length === 0) {
    return null;
  }

  return teams.find((team) => team.value === selectedTeamId) ?? teams[0];
}

export async function buildSwitchedProductSessions(
  input: TenantSwitchInput,
  dependencies: TenantSwitchDependencies,
) {
  const syncSession = await dependencies.getSyncSession(
    input.accessToken,
    input.teamId,
  );
  const selectedTeam = (syncSession?.result?.teamsInformation ?? []).find(
    (team: SelectableTeam) => String(team.value) === String(input.teamId),
  );

  if (!selectedTeam?.name) {
    throw new Error("Selected team is not available for the current user");
  }

  if (!hasIntegradorApp(syncSession)) {
    return {
      integradorSession: null,
      integradorUnavailable: false,
      syncSession,
      token: null,
    };
  }

  try {
    const token = await dependencies.switchIntegradorTenant(
      selectedTeam.name,
      input.accessToken,
    );
    const integradorSession = await dependencies.getIntegradorSession(token);

    return {
      integradorSession,
      integradorUnavailable: !integradorSession?.result,
      syncSession,
      token,
    };
  } catch {
    // Sync owns app entitlements. An unavailable Integrador projection must not
    // discard the selected team's authoritative Sync session.
    return {
      integradorSession: null,
      integradorUnavailable: true,
      syncSession,
      token: null,
    };
  }
}
